/**
 * Sandbox-only mock for the Operator time-tracking + journal experience.
 *
 * Mirrors the shape of `timeTrackingApi` (TimeEntry, OperatorJournalEntry,
 * OperatorTaskLite, OperatorMilestoneLite, OperatorMeetingLite) so the
 * WorkStudio + sidebar indicator can run end-to-end without touching the
 * real Lovable Cloud database.
 *
 * State is persisted in `localStorage` via zustand so a running timer
 * survives navigation/refresh, just like production.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useMockStore } from "./store";
import { ensureTasksRichSeeded, useTasksRichStore } from "./tasksRichStore";
import {
  emitTimerChanged,
  type TimeEntry,
  type OperatorTaskLite,
  type OperatorMilestoneLite,
  type OperatorMeetingLite,
  type OperatorJournalEntry,
} from "../timeTrackingApi";
import type { TimeEntryAttachment } from "../timeEntryEvidenceApi";

/* ---------- Persisted store ---------- */

type State = {
  entries: TimeEntry[];
  journals: Record<string, string>;            // key = `${userId}::${date}` OR legacy `${date}`
  seededFor: string | null;                    // legacy single-user marker
  seededUsers: Record<string, boolean>;        // per-user seeded marker
  attachments: TimeEntryAttachment[];          // sandbox evidence (data: URLs)
  set: (patch: Partial<State>) => void;
};

export const useSandboxTimeTracking = create<State>()(
  persist(
    (set) => ({
      entries: [],
      journals: {},
      seededFor: null,
      seededUsers: {},
      attachments: [],
      set: (patch) => set(patch),
    }),
    { name: "sc-sandbox-time-tracking" },
  ),
);

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID
  ? crypto.randomUUID()
  : `mock-${Math.random().toString(36).slice(2)}-${Date.now()}`);

/* ---------- Helpers ---------- */

function isoMinusDays(daysAgo: number, hour: number, minute = 0): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

/* ---------- Seeding ---------- */

/**
 * Build a realistic week of past entries plus today's journal seed.
 * Idempotent per uid — only seeds when `seededFor` doesn't match.
 */
export function ensureSandboxSeed(userId: string) {
  if (!userId) return;
  const s = useSandboxTimeTracking.getState();
  if (s.seededUsers?.[userId]) return;
  if (s.seededFor === userId && s.entries.some((e) => e.user_id === userId)) {
    // Legacy state — mark as seeded and exit.
    useSandboxTimeTracking.setState({
      seededUsers: { ...(s.seededUsers ?? {}), [userId]: true },
    });
    return;
  }

  const st = useMockStore.getState();
  const projects = st.projects.slice(0, 6);
  if (projects.length === 0) return;

  const tasksByProject = new Map<string, typeof st.tasks>();
  for (const p of projects) {
    tasksByProject.set(
      p.id,
      st.tasks.filter((t) => t.project_id === p.id && t.status !== "completed").slice(0, 6),
    );
  }

  // Build entries for the last 7 days (excluding today, so the user can
  // see "no time logged today" and start their own). Each day mixes 2–4
  // entries across 2 projects with a believable cadence.
  const entries: TimeEntry[] = [];
  const push = (
    project_id: string,
    task_id: string | null,
    daysAgo: number,
    startHour: number,
    durationMin: number,
    note: string | null,
  ) => {
    const started = isoMinusDays(daysAgo, startHour, 0);
    const ended = new Date(new Date(started).getTime() + durationMin * 60_000).toISOString();
    entries.push({
      id: uid(),
      user_id: userId,
      project_id,
      task_id,
      started_at: started,
      ended_at: ended,
      duration_seconds: durationMin * 60,
      note,
      created_at: started,
      updated_at: ended,
    });
  };

  // 6 days ago — kickoff Monday
  const p0 = projects[0];
  const p1 = projects[1] ?? projects[0];
  const p2 = projects[2] ?? p0;
  const p3 = projects[3] ?? p1;

  const t0 = tasksByProject.get(p0.id)?.[0]?.id ?? null;
  const t1 = tasksByProject.get(p1.id)?.[0]?.id ?? null;
  const t2 = tasksByProject.get(p2.id)?.[0]?.id ?? null;
  const t3 = tasksByProject.get(p3.id)?.[0]?.id ?? null;
  const t0b = tasksByProject.get(p0.id)?.[1]?.id ?? null;
  const t1b = tasksByProject.get(p1.id)?.[1]?.id ?? null;

  // Daily rhythm: morning deep-work, midday meeting, afternoon focus.
  push(p0.id, t0,  6, 9, 95,  "Setup del sprint y revisión de tickets.");
  push(p1.id, t1,  6, 11, 60, "Pairing con frontend para integración.");
  push(p0.id, t0b, 6, 14, 110, null);

  push(p1.id, t1,  5, 9, 80,  "Bugfix en webhook de pagos.");
  push(p2.id, t2,  5, 11, 75, "Review del PR de autenticación.");
  push(p0.id, t0,  5, 14, 100, "Refactor del módulo de notificaciones.");

  push(p0.id, t0b, 4, 9, 120, "Implementación de cache distribuido.");
  push(p3.id, t3,  4, 13, 90, "Demo interna + ajustes post-feedback.");

  push(p2.id, t2,  3, 10, 60,  null);
  push(p1.id, t1b, 3, 11, 90,  "Ajustes UX en flujo de onboarding.");
  push(p0.id, t0,  3, 15, 100, "Sesión de planning Q3.");

  push(p3.id, t3,  2, 9, 95,   "Migración del cluster a la nueva región.");
  push(p0.id, t0b, 2, 14, 75,  "Documentación técnica para el cliente.");

  push(p1.id, t1,  1, 9, 110,  "Despliegue a staging + smoke tests.");
  push(p2.id, t2,  1, 12, 50,  "Quick sync con el cliente.");
  push(p0.id, t0,  1, 14, 130, "Cierre de tickets de QA.");

  const todayISOdate = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();

  useSandboxTimeTracking.setState((prev) => ({
    entries: [...prev.entries, ...entries],
    journals: {
      ...prev.journals,
      [todayISOdate]:
        prev.journals[todayISOdate] ??
        "• Sesión enfocada al deploy de staging.\n• Bloqueador: esperando feedback del cliente sobre el flujo de onboarding.\n• Hoy quiero cerrar el bug del polígono y revisar el PR de RBAC.",
      // Persistent scratchpad keyed by 1970-01-01 (per-user via separate journals map below).
      [`${userId}::1970-01-01`]:
        prev.journals[`${userId}::1970-01-01`] ??
        (userId === "tp-116"
          ? "• Revisar contratos del proyecto DeFi antes del jueves.\n• Idea: extraer helper de verificación de firmas para reutilizar entre módulos.\n• Pendiente: agendar 1:1 con el equipo de auditoría.\n• Link útil: https://book.getfoundry.sh/"
          : ""),
    },
    seededFor: userId,
    seededUsers: { ...(prev.seededUsers ?? {}), [userId]: true },
  }));
}

/* ---------- Timer ---------- */

export function getActiveTimerMock(userId: string): TimeEntry | null {
  if (!userId) return null;
  const e = useSandboxTimeTracking.getState().entries.find(
    (x) => x.user_id === userId && x.ended_at === null,
  );
  return e ?? null;
}

export function startTimerMock(p: {
  user_id: string;
  project_id: string;
  task_id?: string | null;
  note?: string | null;
}): TimeEntry {
  const now = new Date().toISOString();
  // Stop any existing active timer first.
  const existingActive = getActiveTimerMock(p.user_id);
  if (existingActive) {
    stopTimerMock(existingActive.id);
  }
  const entry: TimeEntry = {
    id: uid(),
    user_id: p.user_id,
    project_id: p.project_id,
    task_id: p.task_id ?? null,
    started_at: now,
    ended_at: null,
    duration_seconds: null,
    note: p.note ?? null,
    created_at: now,
    updated_at: now,
  };
  useSandboxTimeTracking.setState((s) => ({ entries: [entry, ...s.entries] }));
  emitTimerChanged();
  return entry;
}

export function stopTimerMock(entryId: string, note?: string | null): void {
  const now = new Date().toISOString();
  useSandboxTimeTracking.setState((s) => ({
    entries: s.entries.map((e) => {
      if (e.id !== entryId) return e;
      const ended = now;
      const dur = Math.max(
        0,
        Math.floor((new Date(ended).getTime() - new Date(e.started_at).getTime()) / 1000),
      );
      return {
        ...e,
        ended_at: ended,
        duration_seconds: dur,
        note: note !== undefined ? note : e.note,
        updated_at: ended,
      };
    }),
  }));
  emitTimerChanged();
}

export function updateEntryMock(id: string, patch: Partial<TimeEntry>): void {
  useSandboxTimeTracking.setState((s) => ({
    entries: s.entries.map((e) => (e.id === id ? { ...e, ...patch, updated_at: new Date().toISOString() } : e)),
  }));
  emitTimerChanged();
}

export function deleteEntryMock(id: string): void {
  useSandboxTimeTracking.setState((s) => ({ entries: s.entries.filter((e) => e.id !== id) }));
  emitTimerChanged();
}

export function listEntriesInRangeMock(userId: string, fromISO: string, toISO: string): TimeEntry[] {
  if (!userId) return [];
  const from = new Date(fromISO).getTime();
  const to = new Date(toISO).getTime();
  return useSandboxTimeTracking
    .getState()
    .entries.filter((e) => {
      if (e.user_id !== userId) return false;
      const t = new Date(e.started_at).getTime();
      return t >= from && t < to;
    })
    .sort((a, b) => b.started_at.localeCompare(a.started_at));
}

/* ---------- Journal ---------- */

export function getJournalMock(userId: string, dateISO: string): OperatorJournalEntry | null {
  if (!userId) return null;
  const j = useSandboxTimeTracking.getState().journals;
  // Prefer per-user namespaced key, fall back to legacy global date key.
  const content = j[`${userId}::${dateISO}`] ?? j[dateISO];
  if (content === undefined) return null;
  return {
    id: `${userId}-${dateISO}`,
    user_id: userId,
    entry_date: dateISO,
    content,
    updated_at: new Date().toISOString(),
  };
}

export function saveJournalMock(userId: string, dateISO: string, content: string): void {
  useSandboxTimeTracking.setState((s) => ({
    journals: { ...s.journals, [`${userId}::${dateISO}`]: content },
  }));
}

/* ---------- Operator tasks / milestones / meetings ---------- */

function clientNameForProject(projectId: string): string | null {
  const st = useMockStore.getState();
  const proj = st.projects.find((p) => p.id === projectId);
  if (!proj?.client_id) return null;
  return st.clients.find((c) => c.id === proj.client_id)?.company_name ?? null;
}

/** All tasks for projects the operator is assigned to. */
export function listAssignedTasksMock(userId: string): OperatorTaskLite[] {
  if (!userId) return [];
  ensureTasksRichSeeded();
  const st = useMockStore.getState();
  const projects = st.projects;
  const pmap = new Map(projects.map((p) => [p.id, p.name]));
  // Read from the rich tasks store so edits made via the task sheet
  // (status changes, renames, etc.) are reflected here in real time.
  const richTasks = useTasksRichStore.getState().tasks;
  // Sandbox personas map to seeded team profiles:
  //   Operator (Aleksei) → tp-116
  // Accept either the auth uid or the mapped team_profile_id when
  // matching assignees, so the widget shows the impersonated user's tasks.
  const SANDBOX_OPERATOR_UID = "00000000-0000-0000-0000-0000000ops01";
  const acceptedIds = new Set<string>([userId]);
  if (userId === SANDBOX_OPERATOR_UID) acceptedIds.add("tp-116");
  return richTasks
    .filter((t) => pmap.has(t.project_id))
    .filter((t) => t.assignees.some((a) => acceptedIds.has(a.user_id)))
    .map((t) => ({
      id: t.id,
      project_id: t.project_id,
      project_name: pmap.get(t.project_id) ?? "—",
      client_name: clientNameForProject(t.project_id),
      title: t.title,
      status: t.status === "completed" ? "done" : (t.status as any),
      deadline_at: t.deadline_at ?? null,
      completed_at: t.completed_at ?? null,
      milestone_id: t.milestone_id,
    }));
}

export function listUpcomingMilestonesMock(_userId: string): OperatorMilestoneLite[] {
  const st = useMockStore.getState();
  const pmap = new Map(st.projects.map((p) => [p.id, p.name]));
  return st.milestones
    .filter((m) => m.status !== "completed" && m.status !== "done" && pmap.has(m.project_id))
    .map((m) => ({
      id: m.id,
      project_id: m.project_id,
      project_name: pmap.get(m.project_id) ?? "—",
      title: m.title,
      due_date: (m as any).due_date ?? null,
      status: m.status,
    }))
    .sort((a, b) => {
      const ad = a.due_date ? new Date(a.due_date).getTime() : Infinity;
      const bd = b.due_date ? new Date(b.due_date).getTime() : Infinity;
      return ad - bd;
    })
    .slice(0, 8);
}

export function listUpcomingMeetingsMock(_userId: string): OperatorMeetingLite[] {
  // Synthetic upcoming meetings using the first few projects.
  const st = useMockStore.getState();
  const projects = st.projects.slice(0, 3);
  if (projects.length === 0) return [];
  const seeds = [
    { titleEs: "Daily standup", inHours: 18, dur: 30 },
    { titleEs: "Demo de sprint", inHours: 26, dur: 45 },
    { titleEs: "Sync con el cliente", inHours: 50, dur: 30 },
    { titleEs: "Planning Q3", inHours: 74, dur: 60 },
  ];
  return seeds.slice(0, projects.length + 1).map((m, i) => {
    const p = projects[i % projects.length];
    const starts = new Date(Date.now() + m.inHours * 3600_000).toISOString();
    return {
      id: `mtg-${i}-${p.id}`,
      project_id: p.id,
      project_name: p.name,
      title: m.titleEs,
      starts_at: starts,
      duration_min: m.dur,
      join_url: "https://meet.google.com/sandbox-demo",
      status: "scheduled",
    };
  });
}

/* ---------- Convenience: meta for sidebar indicator ---------- */

export function getTimerMetaMock(entry: TimeEntry): { projectName: string | null; taskTitle: string | null } {
  const st = useMockStore.getState();
  const project = st.projects.find((p) => p.id === entry.project_id);
  const task = entry.task_id ? st.tasks.find((t) => t.id === entry.task_id) : null;
  return { projectName: project?.name ?? null, taskTitle: task?.title ?? null };
}

/* ---------- Evidence (mock) ---------- */

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export async function uploadEvidenceMock(
  userId: string,
  entryId: string,
  file: File,
): Promise<TimeEntryAttachment> {
  const url = await fileToDataUrl(file);
  const att: TimeEntryAttachment = {
    id: uid(),
    entry_id: entryId,
    user_id: userId,
    storage_path: `sandbox/${entryId}/${file.name}`,
    mime: file.type,
    size_bytes: file.size,
    width: null,
    height: null,
    caption: null,
    created_at: new Date().toISOString(),
    url,
  };
  useSandboxTimeTracking.setState((s) => ({ attachments: [...s.attachments, att] }));
  emitTimerChanged();
  return att;
}

export function listEvidenceMock(entryId: string): TimeEntryAttachment[] {
  return useSandboxTimeTracking
    .getState()
    .attachments.filter((a) => a.entry_id === entryId);
}

export function listEvidenceForEntriesMock(entryIds: string[]): Record<string, TimeEntryAttachment[]> {
  const out: Record<string, TimeEntryAttachment[]> = {};
  const ids = new Set(entryIds);
  for (const a of useSandboxTimeTracking.getState().attachments) {
    if (ids.has(a.entry_id)) (out[a.entry_id] ??= []).push(a);
  }
  return out;
}

export function deleteEvidenceMock(id: string): void {
  useSandboxTimeTracking.setState((s) => ({
    attachments: s.attachments.filter((a) => a.id !== id),
  }));
  emitTimerChanged();
}

export function updateEvidenceCaptionMock(id: string, caption: string | null): void {
  useSandboxTimeTracking.setState((s) => ({
    attachments: s.attachments.map((a) => (a.id === id ? { ...a, caption } : a)),
  }));
}

/* ---------- Sample evidence seeding (Aleksei = tp-116) ---------- */

function svgScreenshot(label: string, color: string): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='1280' height='800'>
    <defs>
      <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stop-color='${color}'/>
        <stop offset='100%' stop-color='#0a0a0a'/>
      </linearGradient>
    </defs>
    <rect width='100%' height='100%' fill='url(#g)'/>
    <rect x='40' y='40' width='1200' height='72' fill='rgba(255,255,255,0.10)' rx='10'/>
    <circle cx='80' cy='76' r='14' fill='rgba(255,255,255,0.25)'/>
    <rect x='110' y='66' width='420' height='20' fill='rgba(255,255,255,0.30)' rx='4'/>
    <rect x='40' y='148' width='760' height='612' fill='rgba(255,255,255,0.08)' rx='12'/>
    <rect x='820' y='148' width='420' height='300' fill='rgba(255,255,255,0.08)' rx='12'/>
    <rect x='820' y='460' width='420' height='300' fill='rgba(255,255,255,0.08)' rx='12'/>
    <rect x='72' y='184' width='420' height='28' fill='rgba(255,255,255,0.35)' rx='4'/>
    <rect x='72' y='228' width='680' height='12' fill='rgba(255,255,255,0.18)' rx='3'/>
    <rect x='72' y='252' width='560' height='12' fill='rgba(255,255,255,0.18)' rx='3'/>
    <rect x='72' y='276' width='620' height='12' fill='rgba(255,255,255,0.18)' rx='3'/>
    <text x='72' y='740' font-family='Inter, system-ui, sans-serif' font-size='22' fill='rgba(255,255,255,0.85)' font-weight='600'>${label}</text>
  </svg>`;
  return "data:image/svg+xml;utf8," + encodeURIComponent(svg);
}

/**
 * Seed sample visual evidence for Aleksei (tp-116) so the Admin Studio can
 * showcase how the Evidence widget feels when populated. Idempotent: only
 * runs once per user (skipped if any attachment already exists for them).
 */
export function ensureEvidenceSampleSeed(userId: string) {
  if (userId !== "tp-116") return;
  const st = useSandboxTimeTracking.getState();
  const userEntries = st.entries
    .filter((e) => e.user_id === userId)
    .sort((a, b) => a.started_at.localeCompare(b.started_at));
  if (userEntries.length === 0) return;
  const idSet = new Set(userEntries.map((e) => e.id));
  const alreadyHas = st.attachments.some((a) => idSet.has(a.entry_id));
  if (alreadyHas) return;

  const palette = ["#1e293b", "#0f172a", "#312e81", "#064e3b", "#7c2d12", "#1f2937", "#0c4a6e", "#134e4a", "#3b0764", "#422006"];
  // Spread evidence across roughly the whole week, hitting different days
  // and a couple of entries with 2 attachments to show stacking.
  const samples: Array<{ idx: number; label: string }> = [
    { idx: 0,  label: "Sprint board · Linear" },
    { idx: 0,  label: "Backlog priorizado" },
    { idx: 3,  label: "Webhook log · Stripe" },
    { idx: 5,  label: "PR review · Auth" },
    { idx: 6,  label: "Cache hit ratio dashboard" },
    { idx: 9,  label: "Onboarding flow · Figma" },
    { idx: 11, label: "Cluster migration · K8s" },
    { idx: 11, label: "Health checks · post-migration" },
    { idx: 13, label: "Staging deploy log" },
    { idx: 14, label: "Smoke tests — verde" },
    { idx: 15, label: "Tickets QA cerrados" },
  ];

  const now = new Date().toISOString();
  const atts: TimeEntryAttachment[] = samples
    .filter((s) => userEntries[s.idx])
    .map((s, i) => {
      const e = userEntries[s.idx];
      return {
        id: uid(),
        entry_id: e.id,
        user_id: userId,
        storage_path: `sandbox/${e.id}/sample-${i}.svg`,
        mime: "image/svg+xml",
        size_bytes: 9000,
        width: 1280,
        height: 800,
        caption: s.label,
        created_at: e.started_at ?? now,
        url: svgScreenshot(s.label, palette[i % palette.length]),
      };
    });

  useSandboxTimeTracking.setState((prev) => ({
    attachments: [...prev.attachments, ...atts],
  }));
}